import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { CompanyOffice } from 'src/app/interfaces/models/company-office';
import { Contact } from 'src/app/interfaces/models/contact';
import { License } from 'src/app/interfaces/models/license';
import { Owner } from 'src/app/interfaces/models/owner';
import { Site } from 'src/app/interfaces/models/site';

import { State } from '../../interfaces/state';
import { BaseModel } from './base-model';
import { ClientRegisteredVendor } from './client-registered-vendor';
import { Company } from './company';
import { ExemptVendor } from './exempt-vendor';
import { ProposalRequest } from './proposal-request';
import { Vendor } from './vendor';

export class Address extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static AddressProperties: (
    | {
        property: keyof Address;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof Address;
        type: 'alias';
      }
    | {
        property: keyof Address;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'ExemptVendorId', type: 'column', dataType: 'number' },
    {
      property: 'ClientRegisteredVendorId',
      type: 'column',
      dataType: 'number'
    },
    { property: 'ApVendorId', type: 'column', dataType: 'number' },
    { property: 'SiteId', type: 'column', dataType: 'number' },
    { property: 'ContactId', type: 'column', dataType: 'number' },
    { property: 'OwnerId', type: 'column', dataType: 'number' },
    { property: 'CompanyOfficeId', type: 'column', dataType: 'number' },
    { property: 'LicenseId', type: 'column', dataType: 'number' },
    { property: 'isMailing', type: 'column', dataType: 'boolean' },
    { property: 'isStreet', type: 'column', dataType: 'boolean' },
    { property: 'isRemitTo', type: 'column', dataType: 'boolean' },
    { property: 'country', type: 'column', dataType: 'string' },
    { property: 'address1', type: 'column', dataType: 'string' },
    { property: 'address2', type: 'column', dataType: 'string' },
    { property: 'city', type: 'column', dataType: 'string' },
    { property: 'state', type: 'column', dataType: 'string' },
    { property: 'zip', type: 'column', dataType: 'string' },
    { property: 'latitude', type: 'column', dataType: 'string' },
    { property: 'longitude', type: 'column', dataType: 'string' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'Company', model: 'Company', type: 'association' },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'ExemptVendor', model: 'ExemptVendor', type: 'association' },
    {
      property: 'ClientRegisteredVendor',
      model: 'ClientRegisteredVendor',
      type: 'association'
    },
    { property: 'Contact', model: 'Contact', type: 'association' },
    { property: 'Site', model: 'Site', type: 'association' },
    { property: 'Owner', model: 'Owner', type: 'association' },
    { property: 'CompanyOffice', model: 'CompanyOffice', type: 'association' },
    { property: 'License', model: 'License', type: 'association' },
    {
      property: 'ProposalRequest',
      model: 'ProposalRequest',
      type: 'association'
    },
    {
      property: 'SubNationalDivision',
      model: 'SubNationalDivision',
      type: 'association'
    }
  ];

  static {
    Address.AddressProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        Address.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        Address.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        Address.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(AddressObject: Partial<Address>) {
    super();

    Address.AddressProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = AddressObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  CompanyId?: number | null;
  VendorId?: number | null;
  ExemptVendorId?: number | null;
  ClientRegisteredVendorId?: number | null;
  ApVendorId?: number | null;
  SiteId?: number | null;
  ContactId?: number | null;
  OwnerId?: number | null;
  CompanyOfficeId?: number | null;
  LicenseId?: number | null;
  isMailing?: boolean;
  isStreet?: boolean;
  isRemitTo?: boolean;
  country?: 'US' | 'CA';
  address1?: string;
  address2?: string | null;
  city?: string;
  state?: State;
  zip?: string;
  latitude?: string;
  longitude?: string;
  createdAt?: Date;
  updatedAt?: Date;

  Company?: Company;
  Vendor?: Vendor;
  ExemptVendor?: ExemptVendor;
  ClientRegisteredVendor?: ClientRegisteredVendor;
  Contact?: Contact;
  Site?: Site;
  Owner?: Owner;
  CompanyOffice?: CompanyOffice;
  License?: License;
  ProposalRequest?: ProposalRequest;
  SubNationalDivision?: SubNationalDivision;
}
